import { useMemo, useState } from 'react';
import { useTabState } from '../lib/tabParam';
import { useGroupCount, groupTabLabel } from '../lib/groups';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input } from '../atoms';
import { Tabs, DataTable, StatCard, CreateEntityModal, Modal, useToast, EntityNotesPanel, EntityGroupsPanel, DocumentUploadModal, LastUpdated, InlineEditCard } from '../organisms';
import { EntityHeader } from '../shell';
import { formatDate } from '../lib/orgTimezone';
import { latestAttr, registryOptions, humanizeToken, type AttrMap } from '../lib/inlineAttribution';
import { userNameFrom, type DirectoryUser } from '../lib/userDirectory';
import { useEntityPermission } from '../lib/usePermissions';

/**
 * FiledReturnDetail — /v2/filed-returns/:id
 *
 * Bespoke BPP return screen reverse-engineered from the legacy "Return"
 * window: EntityHeader (assessor/owner/status) + KPI row + lifecycle
 * buttons (prepare → review → file → complete; amend) hitting
 * POST /api/workflow/process-return/:id, above sub-tabs: Summary (asset
 * rollup) · Parcel List · Documents · Notes.
 *
 * GL Values + Reconciliation (the depreciation engine) are intentionally
 * deferred — see notes/prds/fs-2-bpp-returns.md.
 */

interface FiledReturn {
  id: string;
  assessor_id: string;
  client_id: string;
  updated_by?: string | null;
  updated_at?: string | null;
  primary_property_id: string;
  primary_parcel_id: string | null;
  account_number: string | null;
  tax_year: number;
  re_pp: 'real_estate' | 'personal_property' | null;
  type: string | null;
  status: ReturnStatus;
  owner_name: string | null;
  due_date: string | null;
  standard_due_date: string | null;
  prepared_by: string | null;
  prepared_on: string | null;
  reviewed_by: string | null;
  reviewed_on: string | null;
  file_on: string | null;
  filed_via: string | null;
  tracking_number: string | null;
  extension_filed: boolean;
  extension_received: boolean;
  extension_status: string | null;
  extension_requested_date: string | null;
  extension_received_date: string | null;
  reported_amount: number | null;
  prior_reported_amount: number | null;
  assessor_value: number | null;
  prior_assessor_value: number | null;
  approved: boolean;
  filed: boolean;
  filed_subject_to_amendment: boolean;
  asset_list_provided: boolean;
  filed_note: string | null;
}

type ReturnStatus =
  | 'pending' | 'in_progress' | 'prepared' | 'reviewed'
  | 'filed' | 'filed_amended' | 'complete' | 'cancelled';

interface ReturnAsset {
  id: string;
  asset_class: string | null;
  description: string | null;
  acquisition_year: number | null;
  original_cost: number | null;
  reported_value: number | null;
  useful_life: number | null;
  quantity: number | null;
}

/** Raw rows from the live BPP resources (src/routes/bpp.js). NUMERIC columns
 * arrive as strings from Postgres, hence string | number below. */
interface SourceAsset {
  id: string;
  filed_return_id: string;
  asset_class_id: string | null;
  description: string | null;
  acquisition_year: number | null;
  original_cost: string | number | null;
  gl_code: string | null;
  quantity: string | number | null;
}
interface ReportedAsset {
  id: string;
  filed_return_id: string;
  source_asset_id: string | null;
  asset_class_id: string | null;
  description: string | null;
  acquisition_year: number | null;
  original_cost: string | number | null;
  reported_value: string | number | null;
  depreciation_percent: string | number | null;
}
interface AssetClass {
  id: string;
  code: string;
  name: string;
  useful_life: number | null;
}

/** 13.4 — category depreciation rollup (GET /api/bpp/reports/depreciation-by-category).
 * Money keys (source_cost_total / reported_value_total) are ABSENT for money-blind
 * roles — the backend strips them at the response boundary, so they are optional. */
interface CategoryRow {
  asset_class_id: string | null;
  code: string | null;
  name: string | null;
  count: number;
  source_cost_total?: number | null;
  reported_value_total?: number | null;
  avg_depreciation_percent: number | null;
}
interface CategoryReport {
  filed_return_id: string;
  tax_year: number | null;
  reported_amount?: number | null;
  source_cost_total?: number | null;
  categories: CategoryRow[];
}

/** 13.5 — generate-form result (POST /api/filed-returns/:id/generate-form). */
interface RenditionResult {
  document_id: string;
  view_url: string;
  reported_amount?: number | null;
  unresolved?: string[];
  filename?: string;
}

function num(v: string | number | null | undefined): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

interface ReturnParcel { id: string; parcel_id: string; }
interface Parcel { id: string; account_number: string; name: string | null; }
interface Client { id: string; name: string; }
interface Assessor { id: string; name: string; }

type ReturnAction = 'prepare' | 'review' | 'file' | 'amend' | 'complete' | 'cancel';

const statusVariant: Record<ReturnStatus, 'success' | 'warning' | 'neutral' | 'info' | 'danger'> = {
  pending: 'neutral', in_progress: 'warning', prepared: 'info', reviewed: 'info',
  filed: 'success', filed_amended: 'success', complete: 'success', cancelled: 'danger',
};

const statusLabel: Record<ReturnStatus, string> = {
  pending: 'Pending', in_progress: 'In Progress', prepared: 'Prepared', reviewed: 'Reviewed',
  filed: 'Filed', filed_amended: 'Filed (Amended)', complete: 'Complete', cancelled: 'Cancelled',
};

// filed_returns.status is an org-configurable taxonomy (migration 112 dropped the
// CHECK) — a custom status must still render a labelled pill, not a blank one.
const humanizeStatus = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const labelForStatus = (s: string): string => (s ? statusLabel[s as ReturnStatus] ?? humanizeStatus(s) : '—');
const variantForStatus = (s: string) => statusVariant[s as ReturnStatus] ?? 'neutral';

// Available lifecycle actions per current status (mirrors src/config/actions.js
// + FILED_RETURN_STATUS_FLOW in src/config/enums.js).
const actionsByStatus: Record<ReturnStatus, Array<{ action: ReturnAction; label: string; variant: 'primary' | 'danger' }>> = {
  pending: [{ action: 'prepare', label: 'Mark Prepared', variant: 'primary' }, { action: 'cancel', label: 'Cancel', variant: 'danger' }],
  in_progress: [{ action: 'prepare', label: 'Mark Prepared', variant: 'primary' }, { action: 'cancel', label: 'Cancel', variant: 'danger' }],
  prepared: [{ action: 'review', label: 'Mark Reviewed', variant: 'primary' }, { action: 'cancel', label: 'Cancel', variant: 'danger' }],
  reviewed: [{ action: 'file', label: 'File Return', variant: 'primary' }, { action: 'cancel', label: 'Cancel', variant: 'danger' }],
  filed: [{ action: 'complete', label: 'Mark Complete', variant: 'primary' }, { action: 'amend', label: 'File Amendment', variant: 'primary' }],
  filed_amended: [{ action: 'complete', label: 'Mark Complete', variant: 'primary' }],
  complete: [],
  cancelled: [],
};

const actionLabel: Record<ReturnAction, string> = {
  prepare: 'Mark Prepared', review: 'Mark Reviewed', file: 'File Return',
  amend: 'File Amendment', complete: 'Mark Complete', cancel: 'Cancel',
};

function money(v: number | null): string {
  return v != null ? `$${v.toLocaleString()}` : '—';
}

export function FiledReturnDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useTabState(['summary', 'depreciation', 'parcels', 'documents', 'notes', 'groups'] as const, 'summary');
  const groupCount = useGroupCount('filed_return', id);
  const [editOpen, setEditOpen] = useState(false);
  const [confirm, setConfirm] = useState<ReturnAction | null>(null);
  // §6.3 — filing captures a Filing Method + Tracking number at the moment the
  // return moves reviewed → filed (and locks).
  const [filingMethod, setFilingMethod] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  // Capability gates (hooks — before any early return). Edit all fields / Generate
  // rendition / the inline-edit cards → PUT filed-returns (filed_return.can_edit);
  // the lifecycle buttons POST /api/workflow/process-return (filed_return.can_process).
  // +Add asset / Import (in SummaryTab) gate on filed_return.can_add there.
  const canEditReturn = useEntityPermission('filed_return', 'can_edit');
  const canProcessReturn = useEntityPermission('filed_return', 'can_process');

  const returnQuery = useQuery<FiledReturn>({
    queryKey: ['filed_returns', id],
    queryFn: ({ signal }) => api.get<FiledReturn>(`/api/filed-returns/${id}`, signal),
    enabled: !!id,
  });

  const ret = returnQuery.data;

  // Live BPP resources (src/routes/bpp.js): source assets are the operator-
  // editable rows; reported assets are engine-written by the depreciation
  // recompute rule. The grid merges them (reported joins source by
  // source_asset_id to supply reported_value). Replaces the dead
  // /api/return-assets endpoint.
  const sourceAssetsQuery = useQuery<SourceAsset[]>({
    queryKey: ['source-assets', id],
    queryFn: ({ signal }) => api.get<SourceAsset[]>(`/api/source-assets?filed_return_id=${id}`, signal),
    enabled: !!id,
  });
  const reportedAssetsQuery = useQuery<ReportedAsset[]>({
    queryKey: ['reported-assets', id],
    queryFn: ({ signal }) => api.get<ReportedAsset[]>(`/api/reported-assets?filed_return_id=${id}`, signal),
    enabled: !!id,
  });
  const assetClassesQuery = useQuery<AssetClass[]>({
    queryKey: ['asset-classes'],
    queryFn: ({ signal }) => api.get<AssetClass[]>('/api/asset-classes', signal),
  });
  const parcelListQuery = useQuery<ReturnParcel[]>({
    queryKey: ['return-parcel-list', id],
    queryFn: ({ signal }) => api.get<ReturnParcel[]>(`/api/return-parcel-list?filed_return_id=${id}`, signal),
    enabled: !!id,
  });
  const documentsQuery = useQuery<Array<Record<string, unknown>>>({
    queryKey: ['return-documents', id],
    queryFn: ({ signal }) => api.get(`/api/documents?filed_return_id=${id}`, signal),
    enabled: !!id,
  });
  // Notes count for the tab badge — shares EntityNotesPanel's query key + the
  // enriched endpoint so the fetch is deduped and any add/edit/delete refreshes both.
  const notesQuery = useQuery<Array<Record<string, unknown>>>({
    queryKey: ['entity-notes', 'filed_returns', id],
    queryFn: ({ signal }) => api.get(`/api/notes-enriched/filed_returns/${id}`, signal),
    enabled: !!id,
  });

  const clientQuery = useQuery<Client>({
    queryKey: ['client', ret?.client_id],
    queryFn: ({ signal }) => api.get<Client>(`/api/clients/${ret!.client_id}`, signal),
    enabled: !!ret?.client_id,
  });
  const assessorQuery = useQuery<Assessor>({
    queryKey: ['assessor', ret?.assessor_id],
    queryFn: ({ signal }) => api.get<Assessor>(`/api/assessors/${ret!.assessor_id}`, signal),
    enabled: !!ret?.assessor_id,
  });

  // A11 — inline-edit support for the Identity + Extension boxes.
  // Users resolve prepared_by/reviewed_by emails → names (B2); parcels + clients
  // feed the searchable Identity pickers (B3); field-attribution stamps each box.
  const usersQuery = useQuery<DirectoryUser[]>({
    queryKey: ['users-list'],
    queryFn: ({ signal }) => api.get('/api/users-list', signal),
  });
  const parcelsQuery = useQuery<Parcel[]>({
    queryKey: ['parcels-options'],
    queryFn: ({ signal }) => api.get<Parcel[]>('/api/parcels', signal),
  });
  const clientsQuery = useQuery<Client[]>({
    queryKey: ['clients-options'],
    queryFn: ({ signal }) => api.get<Client[]>('/api/clients', signal),
  });
  const attrQuery = useQuery<AttrMap>({
    queryKey: ['field-attribution', 'filed_returns', id],
    queryFn: ({ signal }) => api.get<AttrMap>(`/api/field-attribution/filed_returns/${id}`, signal),
    enabled: !!id,
  });
  // 7/26 A2 — the extension Status is its own dropdown, separate from the return Status.
  // Per 7/28 C6 the options are the three fixed values Granted / Complete / Denied
  // (not user-extensible — no inline add).
  const extStatusesQuery = useQuery<string[] | { values: string[] }>({
    queryKey: ['org-settings', 'return-extension-statuses'],
    queryFn: ({ signal }) => api.get('/api/org-settings/return-extension-statuses', signal),
    staleTime: 5 * 60 * 1000,
  });

  const mutation = useMutation<unknown, Error, ReturnAction>({
    mutationFn: (action) => api.post(`/api/workflow/process-return/${id}`, {
      action,
      // On `file`, carry the captured Filing Method + Tracking number so the
      // backend persists them (filed_via / tracking_number) as it locks the return.
      ...(action === 'file' ? { filing_method: filingMethod || null, tracking_number: trackingNumber || null } : {}),
    }),
    onSuccess: (_data, action) => {
      toast({ variant: 'success', message: `Return: ${actionLabel[action].toLowerCase()} done.` });
      queryClient.invalidateQueries({ queryKey: ['filed_returns', id] });
      queryClient.invalidateQueries({ queryKey: ['filed-returns-list'] });
      setConfirm(null);
      setFilingMethod('');
      setTrackingNumber('');
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  // 13.5 — generate the BPP rendition form. The backend recomputes depreciation,
  // renders via form-render, persists a `documents` row (document_type='Rendition'),
  // and stamps fulfillment_method='generate'. On success we open the download and
  // refresh the return + its documents so the new Rendition appears in the tab.
  const generateMutation = useMutation<RenditionResult, Error, void>({
    mutationFn: () => api.post<RenditionResult>(`/api/filed-returns/${id}/generate-form`),
    onSuccess: (result) => {
      const unresolved = result.unresolved?.length
        ? ` (${result.unresolved.length} template field${result.unresolved.length === 1 ? '' : 's'} left blank)`
        : '';
      toast({ variant: 'success', message: `Rendition generated${unresolved}. Download starting…` });
      queryClient.invalidateQueries({ queryKey: ['filed_returns', id] });
      queryClient.invalidateQueries({ queryKey: ['return-documents', id] });
      if (result.view_url) window.location.assign(result.view_url);
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  // Merge source + reported into the grid shape: reported_value comes from the
  // engine-written reported row (joined on source_asset_id); reported rows with
  // no source link (engine-only) are appended so nothing staged is hidden.
  //
  // This useMemo MUST run on every render — BEFORE the loading / error / !ret
  // early returns below. React requires hooks to be called unconditionally and
  // in the same order on every render; a hook placed after a conditional
  // `return` throws "Rendered more hooks than during the previous render" the
  // moment the guard's truthiness flips (e.g. when the query resolves). Its
  // inputs are all `?? []`-guarded, so it is safe to compute before `ret` loads.
  const assets = useMemo<ReturnAsset[]>(() => {
    const classes = new Map((assetClassesQuery.data ?? []).map(c => [c.id, c]));
    const label = (classId: string | null) => {
      const k = classId ? classes.get(classId) : undefined;
      return k ? `${k.code} — ${k.name}` : null;
    };
    const life = (classId: string | null) => (classId ? classes.get(classId)?.useful_life ?? null : null);
    const reportedBySource = new Map<string, ReportedAsset>();
    const orphanReported: ReportedAsset[] = [];
    for (const r of reportedAssetsQuery.data ?? []) {
      if (r.source_asset_id) reportedBySource.set(r.source_asset_id, r);
      else orphanReported.push(r);
    }
    const rows: ReturnAsset[] = (sourceAssetsQuery.data ?? []).map(s => {
      const rep = reportedBySource.get(s.id);
      return {
        id: s.id,
        asset_class: label(s.asset_class_id),
        description: s.description,
        acquisition_year: s.acquisition_year,
        original_cost: num(s.original_cost),
        reported_value: num(rep?.reported_value ?? null),
        useful_life: life(s.asset_class_id),
        quantity: num(s.quantity),
      };
    });
    for (const r of orphanReported) {
      rows.push({
        id: r.id,
        asset_class: label(r.asset_class_id),
        description: r.description,
        acquisition_year: r.acquisition_year,
        original_cost: num(r.original_cost),
        reported_value: num(r.reported_value),
        useful_life: life(r.asset_class_id),
        quantity: null,
      });
    }
    return rows;
  }, [sourceAssetsQuery.data, reportedAssetsQuery.data, assetClassesQuery.data]);

  if (returnQuery.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading return…</p>;
  }
  if (returnQuery.error) {
    const notFound = returnQuery.error instanceof ApiClientError && returnQuery.error.status === 404;
    return (
      <div style={{
        padding: 'var(--sp-5)',
        background: notFound ? 'var(--color-card)' : 'var(--color-status-red-bg)',
        border: '1px solid ' + (notFound ? 'var(--color-border)' : 'var(--color-status-red)'),
        color: notFound ? 'var(--color-ink)' : 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>
        <h2 style={{ margin: '0 0 var(--sp-2)' }}>{notFound ? 'Return not found' : 'Failed to load return'}</h2>
        <p style={{ margin: 0 }}>{(returnQuery.error as Error).message}</p>
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => navigate('/filed-returns')}>
            ← Back to Returns
          </Button>
        </p>
      </div>
    );
  }
  if (!ret) return null;

  const parcels = parcelListQuery.data ?? [];
  const documents = documentsQuery.data ?? [];
  const notes = notesQuery.data ?? [];
  const lifecycle = actionsByStatus[ret.status] ?? [];
  // §6.3 — once filed, the return LOCKS: no more edits or asset changes, only the
  // amend/complete tail (still offered via the lifecycle buttons).
  const locked = ['filed', 'filed_amended', 'complete', 'cancelled'].includes(ret.status);

  // Searchable Identity pickers (B3) + name resolution (B2) + per-box attribution.
  const parcelOptions = (parcelsQuery.data ?? []).map(p => ({ value: p.id, label: `${p.account_number}${p.name ? ` — ${p.name}` : ''}` }));
  const clientOptions = [...(clientsQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)).map(c => ({ value: c.id, label: c.name }));
  const attr: AttrMap = attrQuery.data ?? {};
  const nameFor = (who: string | null): string | null => userNameFrom(usersQuery.data, who);
  const returnInvalidate: unknown[][] = [['filed_returns', ret.id], ['filed-returns-list'], ['field-attribution', 'filed_returns', ret.id]];
  const linkDisplay = (opts: { value: string; label: string }[], base: string) => (v: unknown) => {
    const id = v == null ? '' : String(v);
    const o = opts.find(o => o.value === id);
    if (!o) return '—';
    return <a onClick={() => navigate(`${base}/${id}`)} style={{ color: 'var(--brand-primary)', cursor: 'pointer', fontWeight: 'var(--fw-medium)' }}>{o.label} ↗</a>;
  };

  return (
    <>
      <EntityHeader
        prefix={`Return · ${ret.tax_year}${ret.type ? ` · ${humanizeToken(ret.type)}` : ''}`}
        title={`${ret.account_number ?? 'No account #'}${ret.owner_name ? ` — ${ret.owner_name}` : ''}`}
        subtitle={<>{[assessorQuery.data?.name, clientQuery.data?.name].filter(Boolean).join(' · ') || null}<LastUpdated updatedBy={ret.updated_by} updatedAt={ret.updated_at} /></>}
        statusPills={
          <>
            <Badge variant={variantForStatus(ret.status)} dot>{labelForStatus(ret.status)}</Badge>
            <Badge variant="neutral">{ret.re_pp == null ? '—' : ret.re_pp === 'real_estate' ? 'Real estate' : 'Personal property'}</Badge>
            {ret.extension_filed && <Badge variant="info" dot>Extension filed</Badge>}
          </>
        }
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={() => navigate('/filed-returns')}>← Back</Button>
            {!locked && canEditReturn && <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>Edit all fields</Button>}
            {canEditReturn && (
              <Button
                variant="secondary"
                size="sm"
                loading={generateMutation.isPending}
                onClick={() => generateMutation.mutate()}
              >
                Generate rendition
              </Button>
            )}
            {canProcessReturn && lifecycle.map(a => (
              <Button key={a.action} variant={a.variant} size="sm" onClick={() => setConfirm(a.action)}>
                {a.label}
              </Button>
            ))}
          </>
        }
        kpis={
          <>
            <StatCard
              label="Reported value"
              value={money(ret.reported_amount)}
              caption={ret.prior_reported_amount != null ? `Prior ${money(ret.prior_reported_amount)}` : 'No prior'}
              accent="hunter"
            />
            <StatCard
              label="Assessor value"
              value={money(ret.assessor_value)}
              caption={ret.prior_assessor_value != null ? `Prior ${money(ret.prior_assessor_value)}` : 'No prior'}
              accent="info"
            />
            <StatCard
              label="Deadline"
              value={formatDate(ret.due_date)}
              caption={ret.extension_filed ? `Extension: ${ret.extension_status ?? 'filed'}` : 'No extension'}
              accent="amber"
            />
            <StatCard
              label="Return filed on"
              value={formatDate(ret.file_on)}
              caption={ret.filed_via ? `via ${ret.filed_via}` : 'Not yet filed'}
              accent={ret.filed ? 'green' : 'info'}
            />
          </>
        }
      />

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
        gap: 'var(--sp-4)',
        marginBottom: 'var(--sp-5)',
      }}>
        <InlineEditCard
          title="Identity"
          routePath="filed-returns"
          recordId={ret.id}
          record={ret as unknown as Record<string, unknown>}
          canEdit={!locked && canEditReturn}
          invalidateKeys={returnInvalidate}
          attribution={latestAttr(attr, ['account_number', 'owner_name', 'primary_parcel_id', 'client_id'])}
          fields={[
            { key: 'account_number', label: 'Account #', type: 'text', display: (v) => v ? <span style={{ fontFamily: 'var(--font-mono)' }}>{String(v)}</span> : '—' },
            { key: 'owner_name', label: 'Owner', type: 'text' },
            { key: 'primary_parcel_id', label: 'Primary parcel', type: 'searchable-select', options: parcelOptions, display: linkDisplay(parcelOptions, '/parcels') },
            { key: 'client_id', label: 'Client', type: 'searchable-select', options: clientOptions, display: linkDisplay(clientOptions, '/clients') },
          ]}
        />
        <InfoCard label="Lifecycle">
          <Row label="Status" value={<Badge variant={variantForStatus(ret.status)} dot>{labelForStatus(ret.status)}</Badge>} />
          <Row label="Prepared" value={ret.prepared_on ? `${formatDate(ret.prepared_on)}${ret.prepared_by ? ` · ${nameFor(ret.prepared_by)}` : ''}` : '—'} />
          <Row label="Reviewed" value={ret.reviewed_on ? `${formatDate(ret.reviewed_on)}${ret.reviewed_by ? ` · ${nameFor(ret.reviewed_by)}` : ''}` : '—'} />
          <Row label="Filed on" value={formatDate(ret.file_on)} />
        </InfoCard>
        <InlineEditCard
          title="Extension"
          routePath="filed-returns"
          recordId={ret.id}
          record={ret as unknown as Record<string, unknown>}
          canEdit={!locked && canEditReturn}
          invalidateKeys={returnInvalidate}
          attribution={latestAttr(attr, ['extension_filed', 'extension_status', 'extension_requested_date', 'extension_received', 'extension_received_date'])}
          fields={[
            { key: 'extension_filed', label: 'Filed', type: 'checkbox' },
            { key: 'extension_status', label: 'Status', type: 'select', options: registryOptions(extStatusesQuery.data), display: (v) => (v ? humanizeToken(String(v)) : '—') },
            { key: 'extension_requested_date', label: 'Requested', type: 'date', display: (v) => v ? formatDate(String(v)) : '—' },
            { key: 'extension_received', label: 'Received', type: 'checkbox' },
            { key: 'extension_received_date', label: 'Received date', type: 'date', display: (v) => v ? formatDate(String(v)) : '—' },
          ]}
        />
        <InfoCard label="Filing">
          <Row label="Filing method" value={ret.filed_via || '—'} />
          <Row label="Tracking #" value={ret.tracking_number ? <span style={{ fontFamily: 'var(--font-mono)' }}>{ret.tracking_number}</span> : '—'} />
          <Row label="Subject to amendment" value={ret.filed_subject_to_amendment ? 'Yes' : 'No'} />
          <Row label="Asset list provided" value={ret.asset_list_provided ? 'Yes' : 'No'} />
        </InfoCard>
      </div>

      <Tabs
        activeValue={tab}
        onChange={(v) => setTab(v as typeof tab)}
        items={[
          { value: 'summary', label: 'Summary', count: assets.length },
          { value: 'depreciation', label: 'Depreciation' },
          { value: 'parcels', label: 'Parcel List', count: parcels.length },
          { value: 'documents', label: 'Documents', count: documents.length },
          { value: 'notes', label: 'Notes', count: notes.length },
          { value: 'groups', label: groupTabLabel(groupCount) },
        ]}
      />

      <div style={{ marginTop: 'var(--sp-5)' }}>
        {tab === 'summary' && <SummaryTab assets={assets} loading={sourceAssetsQuery.isLoading || reportedAssetsQuery.isLoading} returnId={ret.id} locked={locked} />}
        {tab === 'depreciation' && <DepreciationTab returnId={ret.id} />}
        {tab === 'parcels' && <ParcelListTab parcels={parcels} loading={parcelListQuery.isLoading} onRowClick={(p) => navigate(`/parcels/${p.parcel_id}`)} />}
        {tab === 'documents' && <ReturnDocumentsTab returnId={ret.id} documents={documents} loading={documentsQuery.isLoading} />}
        {tab === 'notes' && <EntityNotesPanel entityType="filed_returns" entityId={ret.id} />}
        {tab === 'groups' && <EntityGroupsPanel entityType="filed_return" entityId={ret.id} />}
      </div>

      <CreateEntityModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        table="filed_returns"
        routePath="filed-returns"
        recordId={ret.id}
        invalidateKeys={[['filed-returns-list'], ['filed_returns', ret.id]]}
      />

      <Modal
        open={!!confirm}
        onClose={() => { if (!mutation.isPending) setConfirm(null); }}
        title={confirm ? `Confirm: ${actionLabel[confirm]}` : ''}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)} disabled={mutation.isPending}>Cancel</Button>
            <Button
              variant={confirm === 'cancel' ? 'danger' : 'primary'}
              loading={mutation.isPending}
              onClick={() => confirm && mutation.mutate(confirm)}
            >
              {confirm ? actionLabel[confirm] : ''}
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          This will move the return from <strong>{labelForStatus(ret.status)}</strong> to the next state and log an audit entry.
        </p>
        {confirm === 'file' && (
          <div style={{ marginTop: 'var(--sp-4)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
              Filing locks the return. Capture how it was filed:
            </p>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
              Filing method (e.g. Certified)
              <Input value={filingMethod} onChange={(e) => setFilingMethod(e.target.value)} placeholder="Certified mail" disabled={mutation.isPending} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
              Tracking number
              <Input value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} placeholder="9400 1000 0000 0000 0000 00" disabled={mutation.isPending} />
            </label>
          </div>
        )}
      </Modal>
    </>
  );
}

function SummaryTab({ assets, loading, returnId, locked }: { assets: ReturnAsset[]; loading: boolean; returnId: string; locked: boolean }) {
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // +Add asset / Import both POST source_assets → gate on filed_return.can_add.
  const canAdd = useEntityPermission('filed_return', 'can_add');

  const columns = useMemo<ColumnDef<ReturnAsset>[]>(() => [
    { accessorKey: 'asset_class', header: 'Asset Type', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 'description', header: 'Description', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 'acquisition_year', header: 'Acq. Year', cell: ({ getValue }) => (getValue() as number | null) ?? '—' },
    { accessorKey: 'original_cost', header: 'Original Cost', cell: ({ getValue }) => money(getValue() as number | null) },
    { accessorKey: 'reported_value', header: 'Reported Value', cell: ({ getValue }) => money(getValue() as number | null) },
  ], []);

  const totalCost = assets.reduce((s, a) => s + (a.original_cost ?? 0), 0);
  const totalReported = assets.reduce((s, a) => s + (a.reported_value ?? 0), 0);

  return (
    <>
      {!locked && canAdd && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)', marginBottom: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => setImportOpen(true)}>Import from spreadsheet</Button>
          <Button variant="secondary" size="sm" onClick={() => setAddOpen(true)}>+ Add asset</Button>
        </div>
      )}
      {loading ? (
        <p style={{ color: 'var(--color-ink-3)' }}>Loading assets…</p>
      ) : assets.length === 0 ? (
        <Empty message="No assets reported on this return yet." />
      ) : (
        <>
          <DataTable data={assets} columns={columns} />
          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-6)',
            padding: 'var(--sp-3) var(--sp-4)', marginTop: 'var(--sp-2)',
            fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)',
          }}>
            <span>Total original cost: {money(totalCost)}</span>
            <span>Total reported value: {money(totalReported)}</span>
          </div>
        </>
      )}
      <CreateEntityModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        table="source_assets"
        routePath="source-assets"
        defaultValues={{ filed_return_id: returnId }}
        // Creating a source asset auto-recomputes reported_assets AND
        // filed_returns.reported_amount server-side (bpp-recompute-on-source-change),
        // so invalidate the header "Reported value" KPI (['filed_returns']) and the
        // Depreciation tab too — not just the asset grids. Mirrors ImportAssetsModal.
        invalidateKeys={[
          ['source-assets', returnId],
          ['reported-assets', returnId],
          ['filed_returns', returnId],
          ['bpp-depreciation-report', returnId],
        ]}
      />
      <ImportAssetsModal open={importOpen} onClose={() => setImportOpen(false)} returnId={returnId} />
    </>
  );
}

interface ImportAssetsResult {
  imported: number;
  skipped: number;
  errors: Array<{ row: number; error: string; value?: unknown }>;
  warnings: Array<{ row: number; warning: string }>;
  reported_amount?: number | null;
}

/**
 * ImportAssetsModal — 13.3 bulk source-asset import.
 *
 * Wires the spreadsheet-upload UI to the REAL backend importer
 * POST /api/filed-returns/:id/import-assets (src/routes/bpp.js). The operator
 * picks a CSV; we read it client-side and post it as { text } (the endpoint's
 * JSON body variant — the api client is JSON-only), the backend parses each row,
 * inserts source_assets, and recomputes reported_assets + reported_amount. We
 * render the honest per-file result (imported / skipped / per-row errors +
 * warnings) and invalidate the asset + depreciation + return queries so the grid,
 * the depreciation report, and the KPI reported-amount all refresh.
 */
function ImportAssetsModal({ open, onClose, returnId }: { open: boolean; onClose: () => void; returnId: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [fileName, setFileName] = useState<string | null>(null);
  const [text, setText] = useState<string>('');
  const [result, setResult] = useState<ImportAssetsResult | null>(null);

  const reset = () => { setFileName(null); setText(''); setResult(null); };

  const importMutation = useMutation<ImportAssetsResult, Error, string>({
    mutationFn: (csv) => api.post<ImportAssetsResult>(`/api/filed-returns/${returnId}/import-assets`, { text: csv }),
    onSuccess: (res) => {
      setResult(res);
      queryClient.invalidateQueries({ queryKey: ['source-assets', returnId] });
      queryClient.invalidateQueries({ queryKey: ['reported-assets', returnId] });
      queryClient.invalidateQueries({ queryKey: ['filed_returns', returnId] });
      queryClient.invalidateQueries({ queryKey: ['bpp-depreciation-report', returnId] });
      toast({
        variant: res.imported > 0 ? 'success' : 'info',
        message: `Imported ${res.imported} asset${res.imported === 1 ? '' : 's'}${res.skipped ? `, ${res.skipped} skipped` : ''}.`,
      });
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
        ? String((err.body as { error: unknown }).error)
        : err.message;
      toast({ variant: 'error', message: msg });
    },
  });

  const onPick = async (file: File | undefined) => {
    if (!file) return;
    setResult(null);
    setFileName(file.name);
    setText(await file.text());
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!importMutation.isPending) { reset(); onClose(); } }}
      title="Import assets from spreadsheet"
      size="lg"
    >
      <p style={{ margin: '0 0 var(--sp-3)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
        Upload a CSV with columns: <code>description</code>, <code>asset_class_code</code>, <code>acquisition_year</code>, <code>original_cost</code>, <code>gl_code</code>, <code>quantity</code>. Only <code>original_cost</code> is required per row.
      </p>

      <input
        type="file"
        accept=".csv,.tsv,.txt,text/csv"
        onChange={(e) => onPick(e.target.files?.[0])}
        disabled={importMutation.isPending}
        style={{ display: 'block', marginBottom: 'var(--sp-3)' }}
      />
      {fileName && (
        <p style={{ margin: '0 0 var(--sp-3)', fontSize: 'var(--fs-sm)', color: 'var(--color-ink)' }}>
          Selected: <strong>{fileName}</strong>
        </p>
      )}

      {result && (
        <div style={{
          padding: 'var(--sp-3) var(--sp-4)',
          marginBottom: 'var(--sp-3)',
          background: 'var(--color-card)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--r-md)',
          fontSize: 'var(--fs-sm)',
        }}>
          <p style={{ margin: '0 0 var(--sp-1)', fontWeight: 'var(--fw-semibold)' }}>
            Imported {result.imported} · Skipped {result.skipped}
          </p>
          {result.errors.length > 0 && (
            <div style={{ marginTop: 'var(--sp-2)', color: 'var(--color-status-red)' }}>
              <strong>Errors</strong>
              <ul style={{ margin: 'var(--sp-1) 0 0', paddingLeft: 'var(--sp-4)' }}>
                {result.errors.slice(0, 20).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
              </ul>
            </div>
          )}
          {result.warnings.length > 0 && (
            <div style={{ marginTop: 'var(--sp-2)', color: 'var(--color-status-amber)' }}>
              <strong>Warnings</strong>
              <ul style={{ margin: 'var(--sp-1) 0 0', paddingLeft: 'var(--sp-4)' }}>
                {result.warnings.slice(0, 20).map((w, i) => <li key={i}>Row {w.row}: {w.warning}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      <footer style={{
        display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)',
        marginTop: 'var(--sp-5)', paddingTop: 'var(--sp-4)', borderTop: '1px solid var(--color-border)',
      }}>
        <Button variant="secondary" onClick={() => { reset(); onClose(); }} disabled={importMutation.isPending}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        <Button
          variant="primary"
          loading={importMutation.isPending}
          disabled={importMutation.isPending || !text.trim()}
          onClick={() => importMutation.mutate(text)}
        >
          Import
        </Button>
      </footer>
    </Modal>
  );
}

// 13.4 — BPP depreciation-by-category report. Renders the REAL rollup from
// GET /api/bpp/reports/depreciation-by-category (src/routes/bpp.js), with a CSV
// download that hits the sibling .csv endpoint (money-blind roles get money keys
// stripped by the backend on BOTH surfaces, so cells fall back to '—').
function DepreciationTab({ returnId }: { returnId: string }) {
  const reportQuery = useQuery<CategoryReport>({
    queryKey: ['bpp-depreciation-report', returnId],
    queryFn: ({ signal }) => api.get<CategoryReport>(`/api/bpp/reports/depreciation-by-category?filed_return_id=${returnId}`, signal),
    enabled: !!returnId,
  });

  const columns = useMemo<ColumnDef<CategoryRow>[]>(() => [
    { accessorKey: 'code', header: 'Category Code', cell: ({ getValue }) => (getValue() as string) || '—' },
    { accessorKey: 'name', header: 'Category', cell: ({ getValue }) => (getValue() as string) || 'Unclassified' },
    { accessorKey: 'count', header: 'Lines', cell: ({ getValue }) => (getValue() as number) ?? 0 },
    { accessorKey: 'source_cost_total', header: 'Cost Total', cell: ({ getValue }) => money((getValue() as number | null | undefined) ?? null) },
    { accessorKey: 'reported_value_total', header: 'Reported Value', cell: ({ getValue }) => money((getValue() as number | null | undefined) ?? null) },
    {
      accessorKey: 'avg_depreciation_percent',
      header: 'Avg Depr. %',
      cell: ({ getValue }) => {
        const v = getValue() as number | null;
        return v != null ? `${v}%` : '—';
      },
    },
  ], []);

  if (reportQuery.isLoading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading depreciation report…</p>;
  if (reportQuery.error) {
    const err = reportQuery.error as Error;
    return (
      <div style={{
        padding: 'var(--sp-4)', background: 'var(--color-status-red-bg)',
        border: '1px solid var(--color-status-red)', color: 'var(--color-status-red)',
        borderRadius: 'var(--r-lg)',
      }}>Failed to load depreciation report: {err.message}</div>
    );
  }

  const report = reportQuery.data;
  const categories = report?.categories ?? [];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
          Depreciated value by asset category{report?.tax_year ? ` · ${report.tax_year}` : ''}.
        </span>
        <Button
          variant="secondary"
          size="sm"
          disabled={categories.length === 0}
          onClick={() => window.location.assign(`/api/bpp/reports/depreciation-by-category.csv?filed_return_id=${returnId}`)}
        >
          Download CSV
        </Button>
      </div>
      {categories.length === 0 ? (
        <Empty message="No depreciation lines yet. Add assets and recompute to populate the report." />
      ) : (
        <>
          <DataTable data={categories} columns={columns} />
          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-6)',
            padding: 'var(--sp-3) var(--sp-4)', marginTop: 'var(--sp-2)',
            fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)',
          }}>
            {report?.source_cost_total != null && <span>Total cost: {money(report.source_cost_total)}</span>}
            {report?.reported_amount != null && <span>Total reported value: {money(report.reported_amount)}</span>}
          </div>
        </>
      )}
    </>
  );
}

function ParcelListTab({ parcels, loading, onRowClick }: { parcels: ReturnParcel[]; loading: boolean; onRowClick: (p: ReturnParcel) => void }) {
  const columns = useMemo<ColumnDef<ReturnParcel>[]>(() => [
    { accessorKey: 'parcel_id', header: 'Parcel', cell: ({ getValue }) => <span style={{ fontFamily: 'var(--font-mono)' }}>{String(getValue())}</span> },
  ], []);
  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading parcels…</p>;
  if (parcels.length === 0) return <Empty message="No additional parcels linked to this return." />;
  return <DataTable data={parcels} columns={columns} onRowClick={onRowClick} />;
}

/**
 * ReturnDocumentsTab — the return's attached documents + an Upload affordance.
 * Documents are creatable via the shared DocumentUploadModal (POST
 * /api/documents/upload), pre-linked to this return (filed_return_id). Mirrors the
 * client / government Documents surfaces so every documents list has an upload,
 * not just a read-only view. The return's documents query key is not namespaced
 * under ['documents', …], so onUploaded invalidates ['return-documents', id].
 */
function ReturnDocumentsTab({ returnId, documents, loading }: { returnId: string; documents: Array<Record<string, unknown>>; loading: boolean }) {
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  // Upload posts a document (defaultContext filed_return_id) → gate on document.can_add,
  // consistent with DocumentsList / EntityDocumentsPanel.
  const canAddDoc = useEntityPermission('document', 'can_add');
  return (
    <>
      {canAddDoc && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--sp-3)' }}>
          <Button variant="secondary" size="sm" onClick={() => setUploadOpen(true)}>Upload document</Button>
        </div>
      )}
      <SimpleListTab rows={documents} loading={loading} emptyMessage="No documents attached to this return." columns={['name', 'document_type', 'year']} />
      <DocumentUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        defaultContext={{ filed_return_id: returnId }}
        onUploaded={() => queryClient.invalidateQueries({ queryKey: ['return-documents', returnId] })}
      />
    </>
  );
}

function SimpleListTab({ rows, loading, emptyMessage, columns }: { rows: Array<Record<string, unknown>>; loading: boolean; emptyMessage: string; columns: string[] }) {
  const cols = useMemo<ColumnDef<Record<string, unknown>>[]>(() => columns.map(c => ({
    accessorKey: c,
    header: c.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase()),
    cell: ({ getValue }) => {
      const v = getValue();
      if (v == null || v === '') return '—';
      const s = String(v);
      return s.length > 80 ? s.slice(0, 80) + '…' : s;
    },
  })), [columns]);
  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading…</p>;
  if (rows.length === 0) return <Empty message={emptyMessage} />;
  return <DataTable data={rows} columns={cols} />;
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
    }}>
      <h3 style={{
        margin: '0 0 var(--sp-3)',
        fontSize: 'var(--fs-xs)',
        fontWeight: 'var(--fw-semibold)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--ls-wider)',
        color: 'var(--color-ink-3)',
      }}>{label}</h3>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 'var(--sp-3)',
      padding: 'var(--sp-1) 0',
      fontSize: 'var(--fs-sm)',
      borderBottom: '1px solid var(--color-border)',
    }}>
      <span style={{ color: 'var(--color-ink-3)' }}>{label}</span>
      <span style={{ color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)', textAlign: 'right', wordBreak: 'break-word', maxWidth: '60%' }}>{value}</span>
    </div>
  );
}

function Empty({ message }: { message: string }) {
  return (
    <div style={{
      padding: 'var(--sp-6)',
      background: 'var(--color-card)',
      border: '1px dashed var(--color-border)',
      borderRadius: 'var(--r-lg)',
      color: 'var(--color-muted)',
      textAlign: 'center',
      fontSize: 'var(--fs-md)',
    }}>{message}</div>
  );
}
